/**
 * Logic for the affixed (frozen) column configuration of a table.
 */

import {sum} from 'BenchlingUtil';

/**
 * Where a cell sits in the table's affixed-column layout: whether it is stuck to the left edge,
 * how far from that edge it sits, and which of the edge treatments it renders.
 */
export interface TableCellAffixedProps {
  /** Whether the column is frozen. Default is false. */
  isAffixed?: boolean;
  /** Only enabled when a column is frozen and scrolled. Default is false. */
  showAffixedShadow?: boolean;
  /** Must be specified if isAffixed = true. */
  affixedLeftSpacing?: number;
  isLastFrozenColumn?: boolean;
}

/**
 * The affixed-column configuration a cell is placed within. Zero/absent values describe a table
 * with nothing frozen, which is also what a cell rendered outside the scrollable table (a Single
 * Input) is laid out against.
 */
export interface TableAffixedConfig {
  /** The number of leading columns frozen to the left edge. */
  affixedColumnCount?: number;
  /** Whether the user has frozen any columns. */
  hasAnyFrozenColumns?: boolean;
  /** The width (in px) of the leading axis column. */
  firstColumnWidth?: number | null;
  /** The width (in px) of each of the table's data columns. */
  columnWidths?: Array<number> | null;
  /** Whether the affixed columns should cast a shadow over the scrolled content. */
  showAffixedShadow?: boolean;
}

type GetTableCellAffixedPropsParameters = {
  /** The column index of the cell */
  j: number;
  /** If this cell is the anchor of a merge, the max column of the merged region. */
  mergeMaxJ?: number;
  /** The first column index the row is rendered from, or null when it is not sliced. */
  sliceStart: number | null;
} & TableAffixedConfig;

/** Make the props needed to determine if a cell should be affixed (part of a frozen column) */
export function getTableCellAffixedProps({
  j,
  mergeMaxJ,
  sliceStart,
  affixedColumnCount,
  hasAnyFrozenColumns,
  firstColumnWidth,
  showAffixedShadow: parentShowAffixedShadow,
  columnWidths,
}: GetTableCellAffixedPropsParameters): TableCellAffixedProps {
  const slicedJ = j - (sliceStart ?? 0);
  const isAffixed = Boolean(affixedColumnCount && slicedJ < affixedColumnCount);
  const affixedLeftSpacing =
    columnWidths != null ? (firstColumnWidth ?? 0) + sum(columnWidths.slice(sliceStart ?? 0, j)) : 0;
  const lastFrozenJ = (sliceStart ?? 0) + (affixedColumnCount ?? 0) - 1;
  const showAffixedShadow = Boolean(
    parentShowAffixedShadow && affixedColumnCount && slicedJ === affixedColumnCount - 1
  );
  const mergeEndsAtLastFrozen = mergeMaxJ != null && mergeMaxJ === lastFrozenJ && j <= lastFrozenJ;
  const isLastFrozenColumn = Boolean(
    hasAnyFrozenColumns && affixedColumnCount && (slicedJ === affixedColumnCount - 1 || mergeEndsAtLastFrozen)
  );
  return {
    isAffixed,
    showAffixedShadow,
    affixedLeftSpacing,
    isLastFrozenColumn,
  };
}

/**
 * Whether the table has any user-frozen columns.
 *
 * This is deliberately narrower than `TableUtil.getAffixedColumnCount()`, which also counts the
 * adapter-supplied `defaultAffixedColumnCount`. Only user-frozen columns get the "last frozen
 * column" divider, so an adapter default that affixes columns does not make this true.
 */
export function computeHasAnyFrozenColumns(frozenColumnCount: number | null | undefined): boolean {
  return (frozenColumnCount ?? 0) > 0;
}
